// Format number to Indian currency format
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '₹0';
  
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  
  return formatter.format(amount);
};

// Format currency in a PDF-safe way across currencies.
export const formatPdfCurrency = (currency = 'INR', value) => {
  const safeValue = Number(value ?? 0);
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(safeValue);

  if (currency === 'INR') return `Rs. ${formatted}`;
  if (currency === 'USD') return `$${formatted}`;
  if (currency === 'EUR') return `€${formatted}`;
  if (currency === 'GBP') return `£${formatted}`;
  if (currency === 'AED') return `AED ${formatted}`;

  return `${currency} ${formatted}`;
};

// Format currency for PDF (uses Rs. instead of ₹ for font compatibility)
export const formatCurrencyForPDF = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rs. 0';
  return formatPdfCurrency('INR', amount);
};

// Format number with Indian number system (lakhs, crores)
export const formatIndianNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return new Intl.NumberFormat('en-IN').format(Math.round(num));
};

// EMI Calculation
export const calculateEMI = (principal, annualRate, tenureMonths) => {
  const monthlyRate = annualRate / 12 / 100;
  if (monthlyRate === 0) return principal / tenureMonths;
  
  const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths) / 
              (Math.pow(1 + monthlyRate, tenureMonths) - 1);
  
  return emi;
};

// EMI Schedule Generation
export const generateEMISchedule = (principal, annualRate, tenureMonths) => {
  const monthlyRate = annualRate / 12 / 100;
  const emi = calculateEMI(principal, annualRate, tenureMonths);
  
  const schedule = [];
  let balance = principal;
  
  for (let month = 1; month <= tenureMonths; month++) {
    const interest = balance * monthlyRate;
    const principalPaid = emi - interest;
    balance = balance - principalPaid;
    
    schedule.push({
      month,
      emi: Math.round(emi),
      principal: Math.round(principalPaid),
      interest: Math.round(interest),
      balance: Math.max(0, Math.round(balance)),
    });
  }
  
  return schedule;
};

// SIP Calculation
export const calculateSIP = (monthlyInvestment, annualReturn, years) => {
  const monthlyRate = annualReturn / 12 / 100;
  const months = years * 12;
  
  const futureValue = monthlyInvestment * 
    ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * 
    (1 + monthlyRate);
  
  const totalInvested = monthlyInvestment * months;
  const wealthGained = futureValue - totalInvested;
  
  return {
    futureValue: Math.round(futureValue),
    totalInvested: Math.round(totalInvested),
    wealthGained: Math.round(wealthGained),
  };
};

// Lumpsum Calculation
export const calculateLumpsum = (principal, annualReturn, years) => {
  const futureValue = principal * Math.pow(1 + annualReturn / 100, years);
  const wealthGained = futureValue - principal;
  
  return {
    futureValue: Math.round(futureValue),
    totalInvested: principal,
    wealthGained: Math.round(wealthGained),
  };
};

// PPF Calculation (Public Provident Fund)
export const calculatePPF = (yearlyInvestment, years, interestRate = 7.1) => {
  let balance = 0;
  const yearlyData = [];
  
  for (let year = 1; year <= years; year++) {
    balance += yearlyInvestment;
    const interest = balance * (interestRate / 100);
    balance += interest;
    
    yearlyData.push({
      year,
      investment: yearlyInvestment,
      interest: Math.round(interest),
      balance: Math.round(balance),
    });
  }
  
  const totalInvested = yearlyInvestment * years;
  const totalInterest = balance - totalInvested;
  
  return {
    maturityAmount: Math.round(balance),
    totalInvested,
    totalInterest: Math.round(totalInterest),
    yearlyData,
  };
};

// EPF Calculation (Employee Provident Fund)
export const calculateEPF = (basicSalary, employeeContribution, employerContribution, 
                             currentAge, retirementAge, currentBalance = 0, 
                             salaryIncrement = 5, interestRate = 8.25) => {
  const years = retirementAge - currentAge;
  let balance = currentBalance;
  let salary = basicSalary;
  const yearlyData = [];
  
  for (let year = 1; year <= years; year++) {
    const employeeAmount = (salary * 12 * employeeContribution) / 100;
    const employerAmount = (salary * 12 * employerContribution) / 100;
    const yearlyContribution = employeeAmount + employerAmount;
    
    balance += yearlyContribution;
    const interest = balance * (interestRate / 100);
    balance += interest;
    
    yearlyData.push({
      year,
      salary: Math.round(salary),
      employeeContribution: Math.round(employeeAmount),
      employerContribution: Math.round(employerAmount),
      interest: Math.round(interest),
      balance: Math.round(balance),
    });
    
    salary = salary * (1 + salaryIncrement / 100);
  }
  
  return {
    maturityAmount: Math.round(balance),
    yearlyData,
  };
};

// NPS Calculation (National Pension System)
export const calculateNPS = (monthlyContribution, currentAge, retirementAge, 
                             expectedReturn, annuityPercent = 40) => {
  const years = retirementAge - currentAge;
  const months = years * 12;
  const monthlyRate = expectedReturn / 12 / 100;
  
  const totalCorpus = monthlyContribution * 
    ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * 
    (1 + monthlyRate);
  
  const totalInvested = monthlyContribution * months;
  const annuityAmount = totalCorpus * (annuityPercent / 100);
  const lumpsum = totalCorpus - annuityAmount;
  
  // Estimated monthly pension (assuming 6% annuity rate)
  const monthlyPension = (annuityAmount * 0.06) / 12;
  
  return {
    totalCorpus: Math.round(totalCorpus),
    totalInvested: Math.round(totalInvested),
    wealthGained: Math.round(totalCorpus - totalInvested),
    annuityAmount: Math.round(annuityAmount),
    lumpsumAmount: Math.round(lumpsum),
    estimatedMonthlyPension: Math.round(monthlyPension),
  };
};

// FD Calculation (Fixed Deposit)
export const calculateFD = (principal, annualRate, tenureMonths, compoundingFrequency = 4) => {
  // compoundingFrequency: 1=yearly, 2=half-yearly, 4=quarterly, 12=monthly
  const n = compoundingFrequency;
  const t = tenureMonths / 12;
  const r = annualRate / 100;
  
  const maturityAmount = principal * Math.pow(1 + r / n, n * t);
  const totalInterest = maturityAmount - principal;
  
  return {
    maturityAmount: Math.round(maturityAmount),
    totalInterest: Math.round(totalInterest),
    principal,
  };
};

// RD Calculation (Recurring Deposit)
export const calculateRD = (monthlyDeposit, annualRate, tenureMonths) => {
  const quarterlyRate = annualRate / 4 / 100;
  
  let maturityAmount = 0;
  for (let month = 1; month <= tenureMonths; month++) {
    const remainingQuarters = (tenureMonths - month + 1) / 3;
    const amount = monthlyDeposit * Math.pow(1 + quarterlyRate, remainingQuarters);
    maturityAmount += amount;
  }
  
  const totalInvested = monthlyDeposit * tenureMonths;
  const totalInterest = maturityAmount - totalInvested;
  
  return {
    maturityAmount: Math.round(maturityAmount),
    totalInvested,
    totalInterest: Math.round(totalInterest),
  };
};

// Gratuity Calculation
export const calculateGratuity = (lastSalary, yearsOfService) => {
  // Formula: (Last drawn salary × 15 × Years of service) / 26
  // Last drawn salary = Basic + DA
  const gratuity = (lastSalary * 15 * yearsOfService) / 26;
  const maxGratuity = 2000000; // Maximum gratuity limit is 20 lakhs
  
  return {
    gratuityAmount: Math.round(Math.min(gratuity, maxGratuity)),
    isMaxLimitApplied: gratuity > maxGratuity,
    calculatedAmount: Math.round(gratuity),
  };
};

// HRA Exemption Calculation
export const calculateHRAExemption = (basicSalary, da, hraReceived, rentPaid, isMetro) => {
  const annualBasic = basicSalary * 12;
  const annualDA = da * 12;
  const annualHRA = hraReceived * 12;
  const annualRent = rentPaid * 12;
  
  // Three conditions for HRA exemption
  const condition1 = annualHRA; // Actual HRA received
  const condition2 = annualRent - (0.1 * (annualBasic + annualDA)); // Rent paid - 10% of (Basic + DA)
  const condition3 = isMetro ? 
    0.5 * (annualBasic + annualDA) : // 50% for metro cities
    0.4 * (annualBasic + annualDA);  // 40% for non-metro cities
  
  const exemption = Math.max(0, Math.min(condition1, condition2, condition3));
  const taxableHRA = annualHRA - exemption;
  
  return {
    exemption: Math.round(exemption),
    taxableHRA: Math.round(taxableHRA),
    condition1: Math.round(condition1),
    condition2: Math.round(Math.max(0, condition2)),
    condition3: Math.round(condition3),
  };
};

// Tax Slabs Data for Different Financial Years
export const TAX_SLABS_DATA = {
  // FY 2025-26 (AY 2026-27) - Budget 2025 proposed
  'FY 2025-26': {
    new: {
      standardDeduction: 75000,
      slabs: [
        { min: 0, max: 400000, rate: 0 },
        { min: 400000, max: 800000, rate: 5 },
        { min: 800000, max: 1200000, rate: 10 },
        { min: 1200000, max: 1600000, rate: 15 },
        { min: 1600000, max: 2000000, rate: 20 },
        { min: 2000000, max: 2400000, rate: 25 },
        { min: 2400000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 1200000,
      rebateAmount: 60000,
    },
    old: {
      standardDeduction: 50000,
      slabs: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 500000,
      rebateAmount: 12500,
    },
  },
  // FY 2024-25 (AY 2025-26)
  'FY 2024-25': {
    new: {
      standardDeduction: 75000,
      slabs: [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 700000, rate: 5 },
        { min: 700000, max: 1000000, rate: 10 },
        { min: 1000000, max: 1200000, rate: 15 },
        { min: 1200000, max: 1500000, rate: 20 },
        { min: 1500000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 700000,
      rebateAmount: 25000,
    },
    old: {
      standardDeduction: 50000,
      slabs: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 500000,
      rebateAmount: 12500,
    },
  },
  // FY 2023-24 (AY 2024-25)
  'FY 2023-24': {
    new: {
      standardDeduction: 50000,
      slabs: [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 600000, rate: 5 },
        { min: 600000, max: 900000, rate: 10 },
        { min: 900000, max: 1200000, rate: 15 },
        { min: 1200000, max: 1500000, rate: 20 },
        { min: 1500000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 700000,
      rebateAmount: 25000,
    },
    old: {
      standardDeduction: 50000,
      slabs: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 500000,
      rebateAmount: 12500,
    },
  },
  // FY 2022-23 (AY 2023-24)
  'FY 2022-23': {
    new: {
      standardDeduction: 50000,
      slabs: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 750000, rate: 10 },
        { min: 750000, max: 1000000, rate: 15 },
        { min: 1000000, max: 1250000, rate: 20 },
        { min: 1250000, max: 1500000, rate: 25 },
        { min: 1500000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 500000,
      rebateAmount: 12500,
    },
    old: {
      standardDeduction: 50000,
      slabs: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 },
      ],
      rebateLimit: 500000,
      rebateAmount: 12500,
    },
  },
};

// Get available financial years
export const getAvailableFinancialYears = () => {
  return Object.keys(TAX_SLABS_DATA);
};

// Income Tax Calculation (New Regime)
export const calculateIncomeTaxNewRegime = (income, financialYear = 'FY 2024-25') => {
  const yearData = TAX_SLABS_DATA[financialYear]?.new || TAX_SLABS_DATA['FY 2024-25'].new;
  
  let tax = 0;
  const standardDeduction = yearData.standardDeduction;
  const taxableIncome = Math.max(0, income - standardDeduction);
  const slabs = yearData.slabs;
  
  let remainingIncome = taxableIncome;
  const breakdown = [];
  
  for (const slab of slabs) {
    if (remainingIncome <= 0) break;
    
    const taxableInSlab = Math.min(remainingIncome, slab.max - slab.min);
    const taxForSlab = taxableInSlab * (slab.rate / 100);
    tax += taxForSlab;
    
    if (taxableInSlab > 0) {
      breakdown.push({
        slab: slab.max === Infinity ? `Above ₹${formatIndianNumber(slab.min)}` : 
              `₹${formatIndianNumber(slab.min)} - ₹${formatIndianNumber(slab.max)}`,
        rate: `${slab.rate}%`,
        taxableAmount: Math.round(taxableInSlab),
        tax: Math.round(taxForSlab),
      });
    }
    
    remainingIncome -= taxableInSlab;
  }
  
  // Rebate u/s 87A
  let rebate = 0;
  if (taxableIncome <= yearData.rebateLimit) {
    rebate = Math.min(tax, yearData.rebateAmount);
  }
  
  const taxAfterRebate = tax - rebate;
  const cess = taxAfterRebate * 0.04; // 4% Health and Education Cess
  const totalTax = taxAfterRebate + cess;
  
  return {
    grossIncome: income,
    standardDeduction,
    taxableIncome,
    taxBeforeRebate: Math.round(tax),
    rebate: Math.round(rebate),
    taxAfterRebate: Math.round(taxAfterRebate),
    cess: Math.round(cess),
    totalTax: Math.round(totalTax),
    effectiveRate: ((totalTax / income) * 100).toFixed(2),
    breakdown,
    financialYear,
    slabs: yearData.slabs,
  };
};

// Income Tax Calculation (Old Regime)
export const calculateIncomeTaxOldRegime = (income, deductions = {}, financialYear = 'FY 2024-25') => {
  const yearData = TAX_SLABS_DATA[financialYear]?.old || TAX_SLABS_DATA['FY 2024-25'].old;
  
  const {
    section80C = 0,
    section80D = 0,
    section80G = 0,
    hra = 0,
    lta = 0,
    standardDeduction = yearData.standardDeduction,
    otherDeductions = 0,
  } = deductions;
  
  const totalDeductions = Math.min(section80C, 150000) + // 80C limit is 1.5 lakh
                         Math.min(section80D, 75000) + // 80D limit varies
                         section80G + hra + lta + standardDeduction + otherDeductions;
  
  const taxableIncome = Math.max(0, income - totalDeductions);
  const slabs = yearData.slabs;
  
  let tax = 0;
  let remainingIncome = taxableIncome;
  const breakdown = [];
  
  for (const slab of slabs) {
    if (remainingIncome <= 0) break;
    
    const taxableInSlab = Math.min(remainingIncome, slab.max - slab.min);
    const taxForSlab = taxableInSlab * (slab.rate / 100);
    tax += taxForSlab;
    
    if (taxableInSlab > 0) {
      breakdown.push({
        slab: slab.max === Infinity ? `Above ₹${formatIndianNumber(slab.min)}` : 
              `₹${formatIndianNumber(slab.min)} - ₹${formatIndianNumber(slab.max)}`,
        rate: `${slab.rate}%`,
        taxableAmount: Math.round(taxableInSlab),
        tax: Math.round(taxForSlab),
      });
    }
    
    remainingIncome -= taxableInSlab;
  }
  
  // Rebate u/s 87A
  let rebate = 0;
  if (taxableIncome <= yearData.rebateLimit) {
    rebate = Math.min(tax, yearData.rebateAmount);
  }
  
  const taxAfterRebate = tax - rebate;
  const cess = taxAfterRebate * 0.04;
  const totalTax = taxAfterRebate + cess;
  
  return {
    grossIncome: income,
    totalDeductions: Math.round(totalDeductions),
    taxableIncome: Math.round(taxableIncome),
    taxBeforeRebate: Math.round(tax),
    rebate: Math.round(rebate),
    taxAfterRebate: Math.round(taxAfterRebate),
    cess: Math.round(cess),
    totalTax: Math.round(totalTax),
    effectiveRate: ((totalTax / income) * 100).toFixed(2),
    breakdown,
    financialYear,
    slabs: yearData.slabs,
  };
};

// GST Calculation
export const calculateGST = (amount, gstRate, isInclusive = false) => {
  if (isInclusive) {
    // GST is included in the amount
    const baseAmount = amount / (1 + gstRate / 100);
    const gstAmount = amount - baseAmount;
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;
    
    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      cgst: Math.round(cgst * 100) / 100,
      sgst: Math.round(sgst * 100) / 100,
      totalAmount: amount,
      gstRate,
    };
  } else {
    // GST is to be added
    const gstAmount = amount * (gstRate / 100);
    const cgst = gstAmount / 2;
    const sgst = gstAmount / 2;
    const totalAmount = amount + gstAmount;
    
    return {
      baseAmount: amount,
      gstAmount: Math.round(gstAmount * 100) / 100,
      cgst: Math.round(cgst * 100) / 100,
      sgst: Math.round(sgst * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
      gstRate,
    };
  }
};

// Simple Interest Calculation
export const calculateSimpleInterest = (principal, rate, time) => {
  const interest = (principal * rate * time) / 100;
  const totalAmount = principal + interest;
  
  return {
    principal,
    interest: Math.round(interest),
    totalAmount: Math.round(totalAmount),
  };
};

// Compound Interest Calculation
export const calculateCompoundInterest = (principal, rate, time, frequency = 1) => {
  // frequency: 1=yearly, 2=half-yearly, 4=quarterly, 12=monthly
  const amount = principal * Math.pow(1 + rate / (100 * frequency), frequency * time);
  const interest = amount - principal;
  
  return {
    principal,
    interest: Math.round(interest),
    totalAmount: Math.round(amount),
  };
};
